const cities = [
  { name: "Omaha", zips: "68102, 68104, 68105, 68106, 68107, 68108, 68114, 68116, 68118, 68124, 68127, 68130, 68131, 68132, 68134, 68135, 68137, 68144, 68154, 68164" },
  { name: "Bellevue", zips: "68005, 68123, 68147" },
  { name: "Council Bluffs, IA", zips: "51501, 51503" },
  { name: "Elkhorn", zips: "68022" },
  { name: "La Vista / Papillion", zips: "68128, 68046" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from your home, base housing, dorm, or office in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle every detail from there.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and prevent delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function OmahaLocalAreaSection() {
  return (
    <section id="service-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Omaha &amp; Eastern Nebraska
          </h2>
        </div>

        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-[#F7F8FA] rounded-lg p-8 md:p-10 border border-gray-200">
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Omaha, Nebraska is a city that punches well above its weight in international connectivity. Home to five Fortune 500 headquarters — Berkshire Hathaway, Mutual of Omaha, Union Pacific, Kiewit, and Peter Kiewit Sons' — and a major U.S. Strategic Command (STRATCOM) installation at Offutt Air Force Base, Omaha generates international travel demand that rivals much larger metro areas. The presence of these institutions means thousands of professionals, military personnel, and their families need passports on timelines that standard government processing cannot accommodate.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Offutt AFB is the headquarters of U.S. Strategic Command, one of the most consequential military commands in the world. STRATCOM personnel, along with the 55th Wing and other tenant units, deploy and TDY globally on compressed schedules. PCS orders to overseas assignments — particularly to Germany, the United Kingdom, Japan, and South Korea — frequently arrive with 30 days or less to report. Dependent passports must be current, and the discovery that a spouse or child's travel documents have expired often happens at the worst possible moment: after orders arrive and the clock is already ticking.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Beyond the military, Omaha's corporate density creates another layer of urgent passport demand. Insurance and reinsurance executives travel to London, Zurich, and Bermuda for meetings with as little as one week's notice. Union Pacific's international logistics operations require executive travel to Asia and Latin America. And the annual Berkshire Hathaway shareholders meeting draws tens of thousands of international attendees to Omaha — while we are not processing incoming visitors, the business relationships forged at this event generate year-round outbound travel from Omaha's corporate community to destinations around the world.
            </p>
            <p className="text-gray-600 text-base leading-[1.8]">
              Fast Passport Center was built for exactly these scenarios. We provide the expedited, professional passport service that Omaha's diverse traveler base demands: dedicated agents who understand military and STRATCOM timelines, document review that catches errors before they cost you a deployment slot or board presentation, and processing as fast as 24 hours for true emergencies. No driving to Minneapolis through winter ice storms. No booking a flight to Chicago. Just reliable, predictable service from a registered U.S. Department of State courier.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-passport-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Omaha, NE</p>
                  <p className="text-white/60 text-xs mt-0.5">1001 Farnam St, Omaha, NE 68102</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Local office — serving Omaha, Bellevue &amp; Eastern Nebraska
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Courier pickup available throughout Douglas &amp; Sarpy counties</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your home or office in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location, or <strong className="text-white">schedule a drop-off</strong> at our Omaha office. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="bg-[#F7F8FA] rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-white rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono hidden sm:inline">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Eastern Nebraska — start online, ship overnight, and we handle the rest. No need to drive to Minneapolis or Chicago for regional agency appointments.
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Nearby Cities We Serve</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: "Lincoln, NE", href: "/cities/lincoln" },
                    { name: "Des Moines, IA", href: "/cities/des-moines" },
                    { name: "Kansas City, MO", href: "/cities/kansas-city" },
                    { name: "Sioux City, IA", href: "/cities/sioux-city" },
                    { name: "Minneapolis, MN", href: "/cities/minneapolis" },
                    { name: "Council Bluffs", href: "/states/nebraska" },
                  ].map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-white border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                    >
                      {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Omaha Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}